import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Field,
  FieldError,
  FieldHint,
  FieldRow,
  HelperText,
  Input,
  Label,
  Select,
} from '../form-kit';
import { ChipGroup, ToggleChip } from '../toggle-chip/toggle-chip';
import { Stepper } from '../stepper/stepper';
import { CountedTextarea } from '../counted-textarea/counted-textarea';
import { FormModal } from '../modal/form-modal';
import { AppButton } from '../button/app-button';
import { Shake, StatusLabel } from '../../motion';
import toast from '../../utils/toast';
import { focusField } from '../../utils/field-errors';
import { plural } from '../../function-utils/plural';
import {
  PASS_CATEGORIES,
  PASS_CATEGORY_LABELS,
  PassCategory,
  PassHome,
  PassLimits,
  PassSpecInput,
  ResidentPassState,
  VisitorPass,
} from '../../interfaces/visitor-pass';
import {
  addDays,
  addMonths,
  calendarDayOf,
  describeSchedule,
  describeVisit,
  deskDayOf,
  firstDayOnOrAfter,
  isClockTime,
  isRealDate,
  previewOccurrences,
  ScheduleSpec,
  zoneNote,
} from '../../utils/pass-schedule';
import { PASS_ERROR_COPY, readPassError } from '../../utils/pass-errors';
import { FieldErrors, PassScheduleFields, ScheduleFieldsValue } from './pass-schedule-fields';
import {
  Disclosure,
  DisclosureBody,
  Fieldset,
  Form,
  FormAlert,
  FormAlertAnchor,
  Legend,
  Optional,
  QuickAddSlot,
  Required,
  Summary,
  SummaryLead,
  SummaryLine,
} from './pass-editor.styled';

/**
 * THE PASS EDITOR — one form for every place a visitor pass is written
 * (resident-portal RP-2.9).
 *
 * Built for staff (a property manager or the owner adding a pass on a
 * resident's behalf, from the unit screen) and reused by residents in Plan 4,
 * who add only: a home picker when they live in more than one, and the
 * quick-add sentence in the slot above the fields. It is the BODY of a
 * FormModal: `PassEditorModal` below wraps it with the title, the dirty guard
 * and the pinned Save.
 *
 * WHAT IT SENDS is exactly the server's field set (lobbero_be/src/passes/
 * spec.js) — first_name, last_name, company, category, schedule_kind,
 * valid_from, valid_until, days, interval_weeks, window_start, window_end,
 * max_party, note — built field by field, never spread: the server rejects an
 * unknown key, and the host is the caller's to add.
 *
 * WHEN THE SERVER SAYS NO
 *   400 {message, field, fields}  every named field shows its own message, the
 *                                 first one on screen takes the focus, the form
 *                                 shakes once.
 *   409 PASS_LIMIT / PASSES_DISABLED / SKIP_LIMIT
 *                                 a sentence of our own above the fields.
 *   402 SUBSCRIPTION_ENDED        "Your building's Lobbero subscription is
 *                                 inactive." — switched on the CODE; the
 *                                 server's message is desk wording.
 * Never the server's or axios's own text for anything but a field message
 * (spec.js writes those as copy, for exactly this use). The reading itself is
 * utils/pass-errors — one reader for every pass action (RP-9.6); "check your
 * connection" is said only when no answer came back at all.
 */

export const PASS_PARTY_MAX = 10;

/** Company is asked for the visitors who come from one. */
const COMPANY_CATEGORIES: ReadonlySet<PassCategory | ''> = new Set<PassCategory | ''>([
  'contractor',
  'cleaner',
]);

/* ────────────────────────────────  Copy  ──────────────────────────────── */

export const PASS_COPY = {
  // Every refusal a pass action can meet (utils/pass-errors).
  ...PASS_ERROR_COPY,
  checkDetails: 'Some details could not be saved. Check the form and try again.',
  hostGone: 'This resident no longer lives in this unit. Close this and refresh the unit.',
  homeGone: 'That home is no longer on your account. Close this and refresh the page.',
  passGone: 'This pass has ended or is no longer available. Close this and refresh.',
  saveFailed: 'Could not save this visitor pass. Try again.',
  // Only when no answer came back at all.
  saveOffline: 'Could not save this visitor pass. Check your connection and try again.',
  noteHelper: "Don't put door codes or passwords here.",
} as const;

/* ─────────────────────────────  Form model  ───────────────────────────── */

export interface EditorForm extends ScheduleFieldsValue {
  first_name: string;
  last_name: string;
  company: string;
  category: PassCategory | '';
  max_party: number;
  note: string;
}

/** Ends on, filled in for a new regular pass: three months from today,
 *  never past what the server allows. */
export const defaultUntil = (today: string, limits: PassLimits): string => {
  const threeMonths = addMonths(today, 3);
  const ceiling = addDays(today, limits.weekly_max_days);

  return threeMonths > ceiling ? ceiling : threeMonths;
};

/** What the editor reads from a pass it edits: the fields the staff shape
 *  (VisitorPass) and the resident's (ResidentPass) share. */
export type EditablePass = Pick<
  VisitorPass,
  | 'first_name'
  | 'last_name'
  | 'company'
  | 'category'
  | 'schedule_kind'
  | 'valid_from'
  | 'valid_until'
  | 'days'
  | 'interval_weeks'
  | 'window_start'
  | 'window_end'
  | 'max_party'
  | 'note'
  | 'skip_dates'
  | 'status'
  | 'paused_until'
> & { state?: VisitorPass['state'] | ResidentPassState };

export const initialForm = (
  initial: EditablePass | undefined,
  today: string,
  limits: PassLimits
): { form: EditorForm; untilDefaulted: boolean } => {
  if (!initial) {
    return {
      form: {
        first_name: '',
        last_name: '',
        company: '',
        category: '',
        schedule_kind: 'once',
        valid_from: today,
        valid_until: defaultUntil(today, limits),
        days: [],
        interval_weeks: 1,
        any_time: true,
        window_start: '',
        window_end: '',
        max_party: 1,
        note: '',
      },
      untilDefaulted: true,
    };
  }

  const weekly = initial.schedule_kind === 'weekly';
  const hasWindow = isClockTime(initial.window_start) && isClockTime(initial.window_end);

  return {
    form: {
      first_name: initial.first_name || '',
      last_name: initial.last_name || '',
      company: initial.company || '',
      category: initial.category,
      schedule_kind: initial.schedule_kind,
      valid_from: initial.valid_from,
      valid_until: weekly ? initial.valid_until : defaultUntil(today, limits),
      days: weekly ? [...initial.days] : [],
      interval_weeks: weekly ? initial.interval_weeks || 1 : 1,
      any_time: !hasWindow,
      window_start: hasWindow ? (initial.window_start as string) : '',
      window_end: hasWindow ? (initial.window_end as string) : '',
      max_party: initial.max_party || 1,
      note: initial.note || '',
    },
    untilDefaulted: !weekly,
  };
};

const showsCompany = (form: EditorForm): boolean =>
  COMPANY_CATEGORIES.has(form.category) || form.company.trim() !== '';

/** The request body — the server's field set, named one by one. */
export const buildSpec = (form: EditorForm): PassSpecInput => {
  const timed = !form.any_time;
  const spec: PassSpecInput = {
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    company: form.company.trim(),
    category: form.category,
    schedule_kind: form.schedule_kind,
    valid_from: form.valid_from,
    window_start: timed && form.window_start ? form.window_start : null,
    window_end: timed && form.window_end ? form.window_end : null,
    max_party: form.max_party,
    note: form.note,
  };

  if (form.schedule_kind === 'weekly') {
    spec.valid_until = form.valid_until;
    spec.days = [...form.days].sort((a, b) => a - b);
    spec.interval_weeks = form.interval_weeks;
  }

  return spec;
};

/**
 * QUICK-ADD'S FIELDS AS A FORM PATCH. The parser speaks the server's field
 * set; the form keeps a few of those differently (no window = "Any time" on,
 * weekdays imply Regular). Only values the form can hold are taken: anything
 * else is left for the reader to fill in, never guessed.
 */
export const quickAddPatch = (fields: Partial<PassSpecInput>): Partial<EditorForm> => {
  const patch: Partial<EditorForm> = {};
  const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

  if (text(fields.first_name)) patch.first_name = text(fields.first_name);
  if (text(fields.last_name)) patch.last_name = text(fields.last_name);
  if (text(fields.company)) patch.company = text(fields.company);
  if (fields.category && PASS_CATEGORIES.includes(fields.category as PassCategory)) {
    patch.category = fields.category;
  }
  if (fields.schedule_kind === 'once' || fields.schedule_kind === 'weekly') {
    patch.schedule_kind = fields.schedule_kind;
  }
  if (typeof fields.valid_from === 'string' && isRealDate(fields.valid_from)) {
    patch.valid_from = fields.valid_from;
  }
  if (typeof fields.valid_until === 'string' && isRealDate(fields.valid_until)) {
    patch.valid_until = fields.valid_until;
  }
  if (Array.isArray(fields.days)) {
    const days = Array.from(
      new Set(fields.days.filter(day => Number.isInteger(day) && day >= 1 && day <= 7))
    ).sort((a, b) => a - b);
    if (days.length) {
      patch.days = days;
      if (!patch.schedule_kind) patch.schedule_kind = 'weekly';
    }
  }
  const interval = fields.interval_weeks;
  if (typeof interval === 'number' && Number.isInteger(interval) && interval >= 1 && interval <= 4) {
    patch.interval_weeks = interval;
  }
  if (fields.window_start === null || fields.window_end === null) {
    patch.any_time = true;
  } else if (isClockTime(fields.window_start) || isClockTime(fields.window_end)) {
    patch.any_time = false;
    if (isClockTime(fields.window_start)) patch.window_start = fields.window_start as string;
    if (isClockTime(fields.window_end)) patch.window_end = fields.window_end as string;
  }
  const party = fields.max_party;
  if (typeof party === 'number' && Number.isInteger(party) && party >= 1 && party <= PASS_PARTY_MAX) {
    patch.max_party = party;
  }
  if (text(fields.note)) patch.note = text(fields.note);

  return patch;
};

/**
 * The checks worth making before a round trip — the same rules and the same
 * words as spec.js, so a reader sees one voice whichever side caught it. The
 * server still decides.
 */
export const validateForm = (
  form: EditorForm,
  ctx: { today: string; limits: PassLimits; startLocked: boolean; floor?: string }
): FieldErrors => {
  const errors: FieldErrors = {};
  const weekly = form.schedule_kind === 'weekly';

  if (!form.first_name.trim()) errors.first_name = "Enter the visitor's first name";
  if (!form.category) errors.category = 'Choose who this visitor is';

  if (!isRealDate(form.valid_from)) {
    errors.valid_from = weekly ? 'Enter a start date' : 'Choose the day of the visit';
  } else if (!ctx.startLocked && form.valid_from < (ctx.floor && ctx.floor < ctx.today ? ctx.floor : ctx.today)) {
    errors.valid_from = "The start date can't be in the past";
  }

  if (weekly) {
    if (!form.days.length) errors.days = 'Choose at least one day';
    if (!isRealDate(form.valid_until)) {
      errors.valid_until = 'Choose when this pass ends';
    } else if (isRealDate(form.valid_from) && form.valid_until < form.valid_from) {
      errors.valid_until = 'The end date must be on or after the start date';
    }
  }

  if (!form.any_time) {
    if (!form.window_start) errors.window_start = 'Enter a start time, or choose any time';
    if (!form.window_end) errors.window_end = 'Enter an end time, or choose any time';
    if (form.window_start && form.window_end && form.window_start === form.window_end) {
      errors.window_end = "The end time can't be the same as the start time";
    }
  }

  if (form.note.length > ctx.limits.note_max) {
    errors.note = `Use ${ctx.limits.note_max} characters or fewer`;
  }

  return errors;
};

/* ───────────────────────────  Reading a refusal  ──────────────────────── */

/* The reader moved to utils/pass-errors (RP-9.6) so the screens around the
   editor share it without importing a form. Re-exported: the row actions that
   took it from here still do. */
export { readPassError } from '../../utils/pass-errors';
export type { PassErrorReading } from '../../utils/pass-errors';

/* ──────────────────────────────  The editor  ──────────────────────────── */

/** Staff mode: the resident the pass is for. Fixed; the modal names them. */
export interface PassHost {
  occupancy_id: string;
  name: string;
  unit_label?: string;
}

/** What the editor sends beside the spec: the home a resident picked. */
export interface PassSubmitExtra {
  host_occupancy_id?: string;
}

/** What the quick-add slot may do to the form. */
export interface QuickAddApi {
  /** Fills the form from the parser's fields, through the same path a reader's
   *  own edits take (so errors on those fields clear). Returns the form keys
   *  it set. */
  apply: (fields: Partial<PassSpecInput>) => string[];
  /** True while the form is saving. */
  disabled: boolean;
  /** The calendar date and zone of the building the pass is FOR — the home
   *  picked in the form when the resident has more than one. "Tuesdays" and
   *  "tomorrow" count from this date, not from the first home's. */
  today: string;
  zone: string;
  /** The home the pass is for (its occupancy id), when the form knows — sent
   *  with the AI quick-add so the SERVER reads "today" and "Tuesday" on that
   *  home's building clock, not the resident's first home's. */
  hostOccupancyId?: string;
}

export interface PassEditorProps<P = VisitorPass> {
  /**
   * staff       a manager/owner (or a desk the building lets do everything)
   * staff-once  the front desk at a building set to "one-time visits only"
   *             (migration 118): a one-time visit, today or tomorrow, nothing else
   * resident    the resident's own passes (Plan 4)
   */
  mode: 'staff' | 'staff-once' | 'resident';
  /** Staff mode: who the pass is for (fixed, named in the modal subtitle). */
  host?: PassHost;
  /** Resident mode: the homes a new pass may be for. A picker shows when
   *  there is more than one; the first is sent otherwise. */
  homes?: PassHome[];
  /** The server's limits, from the list response. */
  limits: PassLimits;
  /** The pass being edited; left out to add one. */
  initial?: EditablePass;
  /**
   * ADD mode only: fields the new pass starts with (Plan 5's "Make regular" —
   * first name, weekdays, the usual window, ends in three months). Applied to
   * the starting form through quickAddPatch, the path quick-add's own fields
   * take, so it is what the form OPENS with (closing it untouched asks
   * nothing) and those fields are highlighted for one frame. The focus lands on
   * Last name, which a suggestion never carries. Ignored with `initial`.
   */
  prefill?: Partial<PassSpecInput>;
  /** The building's calendar date — what "today" means for every date rule. */
  today: string;
  /** The building's zone — every time shown is in it. */
  zone: string;
  /** Id of the <form>, so a Save outside it (the modal footer) can submit it. */
  formId: string;
  /** Sends the body. Resolves with the saved pass; rejects with the axios error. */
  onSubmit: (spec: PassSpecInput, extra: PassSubmitExtra) => Promise<P>;
  onSaved: (pass: P) => void;
  /** A quiet line under the form, e.g. "The household will be told." */
  notice?: React.ReactNode;
  /** The quick-add slot above the fields: a node, or a render function handed
   *  what it may do to the form. */
  quickAdd?: React.ReactNode | ((api: QuickAddApi) => React.ReactNode);
  onDirtyChange?: (dirty: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
}

const fieldVisible = (key: string, form: EditorForm): boolean => {
  switch (key) {
    case 'first_name':
    case 'last_name':
    case 'category':
    case 'schedule_kind':
    case 'valid_from':
    case 'max_party':
    case 'note':
      return true;
    case 'company':
      return showsCompany(form);
    case 'days':
    case 'interval_weeks':
    case 'valid_until':
      return form.schedule_kind === 'weekly';
    case 'window_start':
    case 'window_end':
      return !form.any_time;
    default:
      return false;
  }
};

/** Which errors a change clears: the field itself, and the ones it hides. */
const clearedBy = (patch: Partial<EditorForm>): string[] => {
  const keys = Object.keys(patch);
  if ('any_time' in patch) keys.push('window_start', 'window_end');
  if ('schedule_kind' in patch) keys.push('days', 'interval_weeks', 'valid_until', 'valid_from');

  return keys;
};

/* Puts the reader on a control: an input by its `name` (focusField, which
   also scrolls it into view), a group by its pressed / checked member, or its
   first button. */
const focusControl = (root: HTMLElement, element: HTMLElement): void => {
  const key = element.getAttribute('name') || element.getAttribute('data-field') || '';
  if (element.matches('input, select, textarea') && key && focusField(key, root)) return;

  const inner = element.matches('input, select, textarea, button')
    ? element
    : element.querySelector<HTMLElement>(
        '[aria-pressed="true"], [aria-checked="true"], button:not(:disabled), input, select, textarea'
      );
  const target = inner || element;
  if (typeof target.scrollIntoView === 'function') {
    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
  target.focus({ preventScroll: true });
};

const possessive = (name: string): string => `${name}'s`;

const homeLabel = (home: PassHome): string =>
  home.unit_label ? `${home.building_name}, unit ${home.unit_label}` : home.building_name;

export const PassEditor = <P = VisitorPass,>({
  mode,
  homes,
  limits,
  initial,
  prefill,
  today: callerToday,
  zone: callerZone,
  formId,
  onSubmit,
  onSaved,
  notice,
  quickAdd,
  onDirtyChange,
  onSavingChange,
}: PassEditorProps<P>): React.ReactElement => {
  const [start] = useState(() => {
    const base = initialForm(initial, callerToday, limits);
    /* The front desk, before 07:00, adding a visitor: almost always somebody
       arriving NOW. The form opens on "Tonight" — the desk day the Expected
       list is showing — rather than on a day that starts at 07:00. */
    if (mode === 'staff-once' && !initial) {
      const desk = deskDayOf(callerZone);
      if (isRealDate(desk) && desk < callerToday) base.form.valid_from = desk;
    }
    if (initial || !prefill) return { ...base, filled: [] as string[] };
    const patch = quickAddPatch(prefill);

    return {
      form: { ...base.form, ...patch },
      // "Ends on" still reads as the default when the prefill chose the default.
      untilDefaulted: !patch.valid_until || patch.valid_until === base.form.valid_until,
      filled: Object.keys(patch),
    };
  });
  const [form, setForm] = useState<EditorForm>(start.form);
  const [untilDefaulted, setUntilDefaulted] = useState(start.untilDefaulted);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [hostId, setHostId] = useState(() => homes?.[0]?.occupancy_id ?? '');
  // Starts on was moved to the first visit ("every 2 weeks", RP-D22): said
  // under the field until the reader sets the date by hand.
  const [startMoved, setStartMoved] = useState(false);
  // The form keys quick-add just set, highlighted for one painted frame.
  const [quickFilled, setQuickFilled] = useState<string[]>(start.filled);
  const savingRef = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  const resident = mode === 'resident';
  const prefilled = start.filled.length > 0;
  // A resident adding, with more than one home to choose from. Editing never
  // moves a pass to another home.
  const pickHome = resident && !initial && (homes?.length ?? 0) > 1;
  /* TWO HOMES CAN BE IN TWO ZONES. The date rules, the preview and quick-add's
     "tomorrow" belong to the building the pass is for — the home picked here —
     not to whichever home the list happened to put first. With one home, or
     when editing, the caller's today and zone stand. */
  const pickedHome = pickHome ? homes?.find(home => home.occupancy_id === hostId) : undefined;
  /* The caller's `today` IS the date in the caller's zone; only a home in
     another zone needs its own. */
  const todayIn = (home?: PassHome): string =>
    home && home.building_timezone && home.building_timezone !== callerZone
      ? calendarDayOf(home.building_timezone)
      : callerToday;
  const zone = pickedHome?.building_timezone || callerZone;
  const today = todayIn(pickedHome);
  const startLocked = !!initial && isRealDate(initial.valid_from) && initial.valid_from <= today;
  // A REGULAR pass that has started cannot become a one-time visit (129).
  const onceLocked = startLocked && initial?.schedule_kind === 'weekly';
  // The earliest day a new pass may start: the building's DESK date (yesterday
  // before 07:00), never later than its calendar date. Read off the zone the
  // editor already holds, so every caller gets it.
  const fromFloor = useMemo(() => {
    const desk = deskDayOf(zone);

    return isRealDate(desk) && desk < today ? desk : today;
  }, [zone, today]);
  const weekly = form.schedule_kind === 'weekly';
  const id = (key: string) => `${formId}-${key}`;
  const errorId = (key: string) => `${formId}-${key}-error`;

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(start.form), [form, start]);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    onSavingChange?.(saving);
  }, [saving, onSavingChange]);

  // AFTER a refused submit has rendered its messages: the first control that
  // objected (in screen order) takes the focus; with no field to point at, the
  // form-level message does.
  useEffect(() => {
    if (attempt === 0) return;
    const root = formRef.current;
    if (!root) return;
    const invalid = root.querySelector<HTMLElement>('[aria-invalid="true"], [data-invalid="true"]');
    if (invalid) {
      focusControl(root, invalid);

      return;
    }
    if (formError) alertRef.current?.focus();
  }, [attempt, formError]);

  // The quick-add highlight lives for one painted frame, then settles. Two
  // frames, not one: an effect flushed before the first paint would clear it
  // before it was ever drawn.
  useEffect(() => {
    if (!quickFilled.length || typeof requestAnimationFrame !== 'function') return undefined;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setQuickFilled([]));
    });

    return () => {
      cancelAnimationFrame(first);
      if (second) cancelAnimationFrame(second);
    };
  }, [quickFilled]);

  const update = (patch: Partial<EditorForm>) => {
    const becomesWeekly = patch.schedule_kind === 'weekly' && !isRealDate(form.valid_until);
    /* "EVERY 2 WEEKS" COUNTS FROM THE START DATE'S OWN WEEK (owner decision
       RP-D22). A start that is not a visiting day would put the first visit up
       to four weeks away, so when the days or the repeat change, Starts on
       moves to the first visiting day on or after it — and the form says so.
       Never while the date itself is being typed, and never on a pass that has
       started. */
    const merged = { ...form, ...patch };
    let movedTo = '';
    if (
      !startLocked &&
      !('valid_from' in patch) &&
      ('interval_weeks' in patch || 'days' in patch || 'schedule_kind' in patch) &&
      merged.schedule_kind === 'weekly' &&
      merged.interval_weeks > 1 &&
      merged.days.length > 0 &&
      isRealDate(merged.valid_from)
    ) {
      const first = firstDayOnOrAfter(merged.valid_from, merged.days);
      if (first !== merged.valid_from) movedTo = first;
    }
    if (movedTo) setStartMoved(true);
    else if ('valid_from' in patch) setStartMoved(false);
    setForm(current => {
      const next = { ...current, ...patch };
      if (movedTo) next.valid_from = movedTo;
      // Filled in on the SWITCH to Regular only — never while somebody is
      // clearing the field to type their own date.
      if (patch.schedule_kind === 'weekly' && !isRealDate(next.valid_until)) {
        next.valid_until = defaultUntil(today, limits);
      }

      return next;
    });
    if ('valid_until' in patch) setUntilDefaulted(false);
    else if (becomesWeekly) setUntilDefaulted(true);

    const cleared = clearedBy(patch);
    setErrors(current => {
      if (!cleared.some(key => current[key])) return current;
      const next = { ...current };
      cleared.forEach(key => delete next[key]);

      return next;
    });
  };

  /* Quick-add fills the form the way typing would — through update(), so the
     same defaults apply (Regular fills Ends on) and the errors on those
     fields clear. It never saves. A name it could not find is the one thing
     the form cannot do without, so the reader is put on First name. */
  const applyQuickAdd = (fields: Partial<PassSpecInput>): string[] => {
    if (savingRef.current) return [];
    const patch = quickAddPatch(fields);
    const keys = Object.keys(patch);
    if (keys.length) {
      update(patch);
      setQuickFilled(keys);
    }
    if (!('first_name' in patch) && formRef.current) focusField('first_name', formRef.current);

    return keys;
  };

  const quickAddSlot =
    typeof quickAdd === 'function'
      ? quickAdd({
          apply: applyQuickAdd,
          disabled: saving,
          today,
          zone,
          hostOccupancyId: (pickHome && hostId) || homes?.[0]?.occupancy_id || undefined,
        })
      : quickAdd;

  /* Another home, perhaps another zone and so another "today". Dates the
     reader has not touched follow the new home's day; anything they chose is
     theirs. */
  const changeHome = (nextId: string) => {
    const nextToday = todayIn(homes?.find(home => home.occupancy_id === nextId));
    setHostId(nextId);
    if (nextToday !== today) {
      setForm(current => ({
        ...current,
        valid_from: current.valid_from === today ? nextToday : current.valid_from,
        valid_until: untilDefaulted ? defaultUntil(nextToday, limits) : current.valid_until,
      }));
    }
    setErrors(current => {
      if (!current.host_occupancy_id) return current;
      const next = { ...current };
      delete next.host_occupancy_id;

      return next;
    });
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (savingRef.current) return;

    const local = validateForm(form, { today, limits, startLocked, floor: fromFloor });
    if (Object.keys(local).length) {
      setErrors(local);
      setFormError('');
      setAttempt(n => n + 1);

      return;
    }

    savingRef.current = true;
    setSaving(true);
    setErrors({});
    setFormError('');
    // A resident's new pass names its home: the one picked, or the only one.
    const extra: PassSubmitExtra = {};
    if (resident && !initial && homes?.length) {
      extra.host_occupancy_id = (pickHome && hostId) || homes[0].occupancy_id;
    }
    try {
      const saved = await onSubmit(buildSpec(form), extra);
      const first = form.first_name.trim();
      const full = [first, form.last_name.trim()].filter(Boolean).join(' ');
      // A resident's new pass at a building that approves them (125) waits.
      const savedStatus = (saved as { status?: string } | null)?.status;
      const waiting = !initial && savedStatus === 'pending';
      // An approved pass whose who or when changed goes back to the manager
      // (migration 129): the resident is told it is waiting again.
      const again = !!initial && savedStatus === 'pending' && initial.status !== 'pending';
      toast.success(
        again
          ? `Sent to your building for approval again: ${first}.`
          : initial
          ? `Changes saved to ${possessive(first)} pass.`
          : waiting
          ? `Sent to your building for approval: ${full}.`
          : `Pass saved for ${full}.`
      );
      onSaved(saved);
    } catch (error) {
      const reading = readPassError(error, {
        limits,
        fallback: PASS_COPY.saveFailed,
        offline: PASS_COPY.saveOffline,
        notFound: initial ? PASS_COPY.passGone : resident ? PASS_COPY.homeGone : PASS_COPY.hostGone,
      });
      const shown: FieldErrors = {};
      const hidden: string[] = [];
      Object.entries(reading.fields).forEach(([key, message]) => {
        if (!message) return;
        if (fieldVisible(key, form) || (key === 'host_occupancy_id' && pickHome)) shown[key] = message;
        else hidden.push(key);
      });
      setErrors(shown);
      setFormError(
        hidden.includes('host_occupancy_id')
          ? resident
            ? PASS_COPY.homeGone
            : PASS_COPY.hostGone
          : hidden.length
          ? PASS_COPY.checkDetails
          : Object.keys(shown).length
          ? ''
          : reading.message
      );
      setAttempt(n => n + 1);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  /* ── the live summary ── */
  const preview = useMemo(() => {
    const spec: ScheduleSpec = {
      schedule_kind: form.schedule_kind,
      valid_from: form.valid_from,
      valid_until: weekly ? form.valid_until : form.valid_from,
      days: form.days,
      interval_weeks: form.interval_weeks,
      window_start: form.any_time ? null : form.window_start,
      window_end: form.any_time ? null : form.window_end,
      skip_dates: initial?.skip_dates ?? [],
      status: initial?.status === 'paused' ? 'paused' : 'active',
      paused_until: initial?.paused_until ?? null,
    };

    let missing = '';
    if (!isRealDate(form.valid_from)) {
      missing = weekly
        ? 'Choose a start date to see the schedule.'
        : 'Choose the day of the visit to see the schedule.';
    } else if (weekly && !form.days.length) {
      missing = 'Choose the visiting days to see the schedule.';
    } else if (weekly && !isRealDate(form.valid_until)) {
      missing = 'Choose an end date to see the schedule.';
    } else if (
      !form.any_time &&
      (!isClockTime(form.window_start) ||
        !isClockTime(form.window_end) ||
        form.window_start === form.window_end)
    ) {
      missing = 'Enter both times, or choose any time.';
    }

    const next = missing ? [] : previewOccurrences(spec, zone, today, 3, Date.now());

    return { spec, missing, next };
  }, [form, weekly, initial, zone, today]);

  const zoneLine = zoneNote(zone);
  const pausedNow = initial?.state === 'paused';
  const hasErrors = Object.keys(errors).length > 0 || !!formError;
  const noteHelperId = id('note-helper');

  return (
    <Form
      id={formId}
      ref={formRef}
      noValidate
      onSubmit={submit}
      data-quick-filled={quickFilled.length ? quickFilled.join(' ') : undefined}
    >
      <Shake active={attempt > 0 && hasErrors} attempt={attempt}>
        {quickAddSlot ? <QuickAddSlot>{quickAddSlot}</QuickAddSlot> : null}

        <FormAlertAnchor ref={alertRef} tabIndex={-1}>
          <FormAlert id={id('form-error')} role="alert" message={formError || undefined} />
        </FormAlertAnchor>

        {pickHome && homes ? (
          <Field>
            <Label htmlFor={id('host_occupancy_id')}>Which home</Label>
            <Select
              id={id('host_occupancy_id')}
              name="host_occupancy_id"
              value={hostId}
              aria-invalid={errors.host_occupancy_id ? true : undefined}
              aria-describedby={errors.host_occupancy_id ? errorId('host_occupancy_id') : undefined}
              disabled={saving}
              onChange={event => changeHome(event.target.value)}
            >
              {homes.map(home => (
                <option key={home.occupancy_id} value={home.occupancy_id}>
                  {homeLabel(home)}
                </option>
              ))}
            </Select>
            <FieldError id={errorId('host_occupancy_id')} message={errors.host_occupancy_id} />
          </Field>
        ) : null}

        <FieldRow>
          <Field>
            <Label htmlFor={id('first_name')}>
              First name
              <Required aria-hidden="true">*</Required>
            </Label>
            <Input
              id={id('first_name')}
              name="first_name"
              value={form.first_name}
              autoComplete="off"
              aria-required="true"
              aria-invalid={errors.first_name ? true : undefined}
              aria-describedby={errors.first_name ? errorId('first_name') : undefined}
              hasError={!!errors.first_name}
              disabled={saving}
              data-autofocus={prefilled ? undefined : true}
              onChange={event => update({ first_name: event.target.value })}
            />
            <FieldError id={errorId('first_name')} message={errors.first_name} />
          </Field>
          <Field>
            <Label htmlFor={id('last_name')}>Last name</Label>
            <Input
              id={id('last_name')}
              name="last_name"
              value={form.last_name}
              autoComplete="off"
              aria-invalid={errors.last_name ? true : undefined}
              aria-describedby={errors.last_name ? errorId('last_name') : undefined}
              hasError={!!errors.last_name}
              disabled={saving}
              data-autofocus={prefilled ? true : undefined}
              onChange={event => update({ last_name: event.target.value })}
            />
            <FieldError id={errorId('last_name')} message={errors.last_name} />
          </Field>
        </FieldRow>

        <Fieldset data-field="category" data-invalid={errors.category ? 'true' : undefined}>
          <Legend>
            Who
            <Required aria-hidden="true">*</Required>
          </Legend>
          <ChipGroup aria-label="Who is visiting">
            {PASS_CATEGORIES.map(category => (
              <ToggleChip
                key={category}
                pressed={form.category === category}
                onToggle={() => update({ category })}
                disabled={saving}
              >
                {PASS_CATEGORY_LABELS[category]}
              </ToggleChip>
            ))}
          </ChipGroup>
          <FieldError id={errorId('category')} message={errors.category} />
        </Fieldset>

        <Disclosure open={showsCompany(form)}>
          <DisclosureBody>
            <Field>
              <Label htmlFor={id('company')}>
                Company
                <Optional>(optional)</Optional>
              </Label>
              <Input
                id={id('company')}
                name="company"
                value={form.company}
                autoComplete="off"
                aria-invalid={errors.company ? true : undefined}
                aria-describedby={errors.company ? errorId('company') : undefined}
                hasError={!!errors.company}
                disabled={saving}
                onChange={event => update({ company: event.target.value })}
              />
              <FieldError id={errorId('company')} message={errors.company} />
            </Field>
          </DisclosureBody>
        </Disclosure>

        <PassScheduleFields
          value={form}
          onChange={update}
          errors={errors}
          limits={limits}
          today={today}
          fromFloor={fromFloor}
          startLocked={startLocked}
          onceLocked={onceLocked}
          startMoved={startMoved}
          untilDefaulted={untilDefaulted}
          idPrefix={formId}
          disabled={saving}
          onceOnly={mode === 'staff-once'}
        />

        <Summary role="status" aria-live="polite" aria-atomic="true">
          {preview.missing ? (
            <SummaryLine>{preview.missing}</SummaryLine>
          ) : (
            <>
              <SummaryLead>{describeSchedule(preview.spec, { today })}</SummaryLead>
              <SummaryLine>
                {preview.next.length
                  ? `Next ${plural(preview.next.length, 'visit')}: ${preview.next
                      .map(occurrence => describeVisit(occurrence, zone, today))
                      .join(' · ')}`
                  : pausedNow
                  ? 'No visits while this pass is paused.'
                  : 'No visits fall between these dates.'}
              </SummaryLine>
              {zoneLine ? <SummaryLine>{zoneLine}</SummaryLine> : null}
            </>
          )}
        </Summary>

        <Fieldset data-field="max_party" data-invalid={errors.max_party ? 'true' : undefined}>
          <Legend>Party size</Legend>
          <Stepper
            label="people"
            min={1}
            max={PASS_PARTY_MAX}
            value={form.max_party}
            onChange={max_party => update({ max_party })}
            disabled={saving}
          />
          <FieldHint>How many people can come in together on this pass.</FieldHint>
          <FieldError id={errorId('max_party')} message={errors.max_party} />
        </Fieldset>

        <Field>
          <CountedTextarea
            id={id('note')}
            name="note"
            label="Note to the front desk"
            value={form.note}
            max={limits.note_max}
            rows={3}
            hasError={!!errors.note}
            aria-describedby={[noteHelperId, errors.note ? errorId('note') : '']
              .filter(Boolean)
              .join(' ')}
            disabled={saving}
            onChange={event => update({ note: event.target.value })}
          />
          <HelperText id={noteHelperId}>{PASS_COPY.noteHelper}</HelperText>
          <FieldError id={errorId('note')} message={errors.note} />
        </Field>

        {notice ? <FieldHint>{notice}</FieldHint> : null}
      </Shake>
    </Form>
  );
};

/* ───────────────────────────────  The modal  ──────────────────────────── */

export interface PassEditorModalProps<P = VisitorPass>
  extends Omit<PassEditorProps<P>, 'formId' | 'onDirtyChange' | 'onSavingChange'> {
  open: boolean;
  onClose: () => void;
  /** Defaults to "Add expected visitor" / "Edit expected visitor" — for a
   *  resident, "Add a visitor" / "Edit visitor pass". */
  title?: string;
  /** Defaults, in staff mode, to naming the host: "For Ana Petrova, unit 4B". */
  subtitle?: React.ReactNode;
}

const hostLine = (host: PassHost): string =>
  host.unit_label ? `For ${host.name}, unit ${host.unit_label}` : `For ${host.name}`;

/**
 * The editor in a FormModal: bottom sheet below 480px, focus trapped, Escape /
 * the scrim / the × all through the discard guard while anything is typed,
 * focus back on the opener when it closes. Save lives in the pinned footer and
 * submits the form by id.
 */
export const PassEditorModal = <P = VisitorPass,>({
  open,
  onClose,
  title,
  subtitle,
  ...editor
}: PassEditorModalProps<P>): React.ReactElement => {
  const rawId = useId();
  const formId = `pass-editor-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const editing = !!editor.initial;

  // A closed editor holds nothing: the next one opens clean.
  useEffect(() => {
    if (!open) {
      setDirty(false);
      setSaving(false);
    }
  }, [open]);

  const heading =
    title ??
    (editor.mode === 'resident'
      ? editing
        ? 'Edit visitor pass'
        : 'Add a visitor'
      : editing
      ? 'Edit expected visitor'
      : 'Add expected visitor');
  const sub =
    subtitle ??
    ((editor.mode === 'staff' || editor.mode === 'staff-once') && editor.host
      ? hostLine(editor.host)
      : undefined);

  return (
    <FormModal
      open={open}
      title={heading}
      subtitle={sub}
      onClose={onClose}
      isDirty={dirty}
      // A save on its way is neither lost nor doubled: ×, Escape and the scrim
      // wait for the answer (RP-9.8).
      saving={saving}
      discardMessage={
        editing
          ? 'Your changes to this pass have not been saved. Close anyway and lose them?'
          : 'This visitor pass has not been saved yet. Close and lose what you have entered?'
      }
      footer={
        <AppButton type="submit" form={formId} disabled={saving}>
          <StatusLabel
            state={saving ? 'busy' : 'idle'}
            labels={{ idle: editing ? 'Save changes' : 'Save pass', busy: 'Saving…' }}
          />
        </AppButton>
      }
    >
      <PassEditor<P>
        {...editor}
        formId={formId}
        onDirtyChange={setDirty}
        onSavingChange={setSaving}
      />
    </FormModal>
  );
};
